import React, { Fragment, useEffect, useState } from "react";
import CommonCard from "../../common/components/CommonCard";
import { Box, Divider, Grid, IconButton, Stack, Typography } from "@mui/material";
import theme from "../../theme";
import { Add, Close, Description, FiberManualRecord } from "@mui/icons-material";
import { ServiceOffer, ServiceTypes, TasksPriority } from "../../common/const";
import CommonButton from "../../common/components/CommonButton";
import CommonDialog from "../../common/components/CommonDialog";
import Dropzone from "react-dropzone";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../features/authSlice";
import { CreateResource, getResources } from "../../api/satactApi";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import LoadingSpinner from "../../common/components/LoadingSpinner";

const Resource = ({
    fromMentor,
    studentId,
    currentService,
    tutoringType,
    subService
}) => {
    const dispatch = useDispatch();
    const { user, loading } = useSelector((state) => state.auth);
    const [resources, setResources] = useState([]);
    const [open, setOpen] = useState(false);
    const [fileNames, setFileNames] = useState([]);
    const [priority, setPriority] = useState('HIGH');

    const getResourceData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getResources(currentService === ServiceOffer.skillCoach ? 'SKILL_COACHING' : subService ? 'SKILL_COACHING' : (ServiceTypes[tutoringType] || currentService), user?.student_id || studentId, subService);
            setResources(response.data.map((a) => ({
                urls: a.document_urls,
                priority: a.priority
            })));
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        if (user?.student_id || studentId) {
            getResourceData();
        }
    }, [tutoringType, currentService, user, fromMentor, subService]);

    const handleDrop = acceptedFiles => {
        setFileNames(acceptedFiles?.map(files => files));
    };

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <CommonDialog
                open={open}
                size="xs"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Box>
                                <Typography sx={{ fontWeight: 600, fontSize: 18 }}>Upload Resources</Typography>
                            </Box>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setFileNames([]);
                                    setOpen(false);
                                }}
                            />
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container mt={2} mb={2}>
                        <Box sx={{ width: '100%' }}>
                            {fileNames.length > 0 ? (
                                <Box sx={{ mt: 2 }}>
                                    {fileNames.map((file, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                p: 2,
                                                border: '1px solid',
                                                borderColor: 'grey.300',
                                                borderRadius: 1,
                                                mb: 1,
                                                backgroundColor: 'background.paper'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Description color="primary" />
                                                <Box>
                                                    <Typography variant="body1">{file.name}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {(file.size / 1024).toFixed(2)} KB
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <IconButton
                                                size="small"
                                                onClick={() => setFileNames(fileNames.splice(index, -1))}
                                                color="error"
                                            >
                                                <Close />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                // Show dropzone when no files
                                <Dropzone
                                    onDrop={handleDrop}
                                >
                                    {({ getRootProps, getInputProps, isDragActive }) => (
                                        <Box
                                            {...getRootProps()}
                                            sx={{
                                                border: '2px dashed',
                                                borderColor: '#D8DAEB',
                                                borderRadius: 2,
                                                padding: { xs: 3, md: 6 },
                                                textAlign: 'center',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                minHeight: 100,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <input {...getInputProps()} />
                                            <img src="/Images/sound.png" alt="upload" />
                                            <Typography fontWeight={600} gutterBottom>
                                                Drag and drop your files
                                            </Typography>
                                            <CommonButton
                                                label="Select File"
                                                color="Black"
                                                background="White"
                                                border={`1px solid ${theme.palette.background.secondary}`}
                                                width="100px"
                                                height="40px"
                                                marginBottom="0px"
                                                marginTop="10px"
                                            />
                                        </Box>
                                    )}
                                </Dropzone>
                            )}
                        </Box>
                        <Grid item sm={12} mt={1}>
                            <Box
                                display="flex"
                                gap={1}
                            >
                                <CommonButton
                                    label='High'
                                    size="small"
                                    height="35px"
                                    marginBottom="0px"
                                    fontWeight="bold"
                                    background={priority === 'HIGH' ? '#FFE2E2' : theme.palette.background.main}
                                    color={priority === 'HIGH' ? 'red' : theme.palette.background.dark}
                                    handleClick={() => {
                                        setPriority('HIGH');
                                    }}
                                    border={priority !== 'HIGH' ? "1px solid lightgray" : '1px solid #FF1010'}
                                />
                                <CommonButton
                                    label='Medium'
                                    size="small"
                                    height="35px"
                                    marginBottom="0px"
                                    fontWeight="bold"
                                    background={priority === 'MEDIUM' ? '#FEF9C2' : theme.palette.background.main}
                                    color={priority === 'MEDIUM' ? '#894B00' : theme.palette.background.dark}
                                    handleClick={() => {
                                        setPriority('MEDIUM');
                                    }}
                                    border={priority !== 'MEDIUM' ? "1px solid lightgray" : '1px solid #894B00'}
                                />
                                <CommonButton
                                    label="Low"
                                    height="35px"
                                    marginBottom="0px"
                                    fontWeight="bold"
                                    background={priority === 'LOW' ? theme.palette.success.light : theme.palette.background.main}
                                    color={priority === 'LOW' ? '#01875F' : theme.palette.background.dark}
                                    handleClick={() => {
                                        setPriority('LOW');
                                    }}
                                    border={priority !== 'LOW' ? "1px solid lightgray" : `1px solid #01875F`}
                                />
                                <CommonButton
                                    label="New"
                                    height="35px"
                                    marginBottom="0px"
                                    fontWeight="bold"
                                    background={priority === 'NEW' ? theme.palette.success.light : theme.palette.background.main}
                                    color={priority === 'NEW' ? '#01875F' : theme.palette.background.dark}
                                    handleClick={() => {
                                        setPriority('NEW');
                                    }}
                                    border={priority !== 'NEW' ? "1px solid lightgray" : `1px solid #01875F`}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                }
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label="Save"
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.background.main}
                            fontWeight={700}
                            marginBottom="10px"
                            handleClick={async () => {
                                try {
                                    dispatch(setLoading({
                                        loading: true
                                    }));
                                    const formData = new FormData();
                                    formData.append('type', currentService === ServiceOffer.skillCoach ? 'SKILL_COACHING' : currentService);
                                    formData.append('created_by', user?.user_id);
                                    formData.append('priority', priority);
                                    fileNames.map((a) => {
                                        formData.append('documents', a);
                                    })
                                    formData.append('student_id', studentId);
                                    formData.append('mentor_profile_id', user?.mentor?.mentor_profile_id);
                                    formData.append('service_offers', currentService);
                                    if (subService) {
                                        formData.append('student_subject_id', subService);
                                    }
                                    const response = await CreateResource(formData);
                                    if (response.success) {
                                        showNotification(response.message, 'success');
                                        setOpen(false);
                                        dispatch(setLoading({
                                            loading: false
                                        }));
                                        setFileNames([]);
                                        getResourceData();
                                    }
                                } catch (err) {
                                    const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                    showNotification(errorMessage, 'error');
                                    dispatch(setLoading({
                                        loading: false
                                    }));
                                }
                            }}
                        />
                    </Fragment>
                )}
            />
            <CommonCard
                borderRadius={2}
                border={`1px solid #0000001A`}
                marginTop={fromMentor ? "0px" : "15px"}
                headerContent={
                    <Fragment>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ padding: '13px' }}
                        >
                            <Box>
                                <Typography fontWeight={600}>{fromMentor ? 'Shared Resources' : 'Study Resources'}</Typography>
                                {!fromMentor && (
                                    <Typography fontSize={14} color={theme.palette.background.dark}>Access helpful materials to support your learning.</Typography>
                                )}
                            </Box>
                            {fromMentor && (
                                <CommonButton
                                    label="Add Resources"
                                    height="35px"
                                    width="160px"
                                    marginTop="0px"
                                    marginBottom="0px"
                                    startIcon={<Add />}
                                    handleClick={() => setOpen(true)}
                                />
                            )}
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Box
                        sx={{
                            height: resources.length > 0 ? '22vh' : '8vh',
                            overflow: 'auto'
                        }}
                    >
                        {resources.length > 0 ?
                            (<Box
                                sx={{
                                    height: '31vh',
                                    overflow: 'auto'
                                }}
                            >
                                {resources.map((a, index) => (
                                    a.urls.map((i) => {
                                        return (
                                            <Fragment key={index}>
                                                <Box
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    padding="13px"
                                                >
                                                    <Box>
                                                        <Typography fontSize={14} fontWeight="600">{i.filename}</Typography>
                                                        <Stack direction="row" alignItems="center" spacing={1} mt={0.4}>
                                                            <Typography color="background.dark" sx={{ fontSize: 13 }}>
                                                                {i.mimetype}
                                                            </Typography>
                                                            <FiberManualRecord sx={{ fontSize: 8, color: 'text.secondary' }} />
                                                            <Typography color="background.dark" sx={{ fontSize: 13 }}>
                                                                {(i.size / 1024).toFixed(2)} KB
                                                            </Typography>
                                                        </Stack>
                                                    </Box>

                                                    {!fromMentor && (
                                                        <Box display="flex" gap={1} alignItems="center" justifyContent="center"
                                                        >
                                                            <Box
                                                                sx={{
                                                                    background: a.priority === 'MEDIUM' ? theme.palette.secondary.main : a.priority === 'HIGH' ? 'red' : '#01875F',
                                                                    padding: '3px 15px',
                                                                    textAlign: 'center',
                                                                    marginBottom: 0.5,
                                                                    borderRadius: 1,
                                                                }}
                                                            >
                                                                <Typography fontSize={13} color={a.priority === 'MEDIUM' ? theme.palette.primary.main : a.priority === 'HIGH' ? 'white' : 'white'}>{TasksPriority[a.priority]}</Typography>
                                                            </Box>
                                                            <a
                                                                href={`${process.env.REACT_APP_BASE_URL}/file?bname=${i?.url}&download=true`}
                                                                download
                                                                target="_blank"
                                                                style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
                                                            >
                                                                <img src="/Images/receive-square.png" alt="download" width="25px" height="25px" />
                                                            </a>
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Divider />
                                            </Fragment>
                                        )
                                    })
                                ))}
                            </Box>) : (
                                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                                    <Typography color={theme.palette.background.dark}>No Resource Found</Typography>
                                </Box>
                            )}
                    </Box>
                }
                paddingBottom="0px"
                padding="13px"
            />
        </Fragment>
    )
}

export default Resource;